import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
    Component,
    effect,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    Output,
    Signal,
    signal,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import { FormControlStatus, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ButtonType } from 'app/core/dialogBuilder/dialog-builder.models';
import { DialogBuilderService } from 'app/core/dialogBuilder/dialog-builder.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Table, TableColumnDef } from '../table';
import { TableFilterComponentComponent } from './table-filter-component/table-filter-component.component';
import {
    TableAdvencedFilterModel,
    TableFilterComponentModel,
} from './table-filter-component/table-filter-component.model';
import { TableFilterService } from './table-filter-component/table-filter.service';
import { TableStateService } from './tableState.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ColumnOrderComponent } from './column-order/column-order.component';
import { TranslocoService } from '@ngneat/transloco';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'custom-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: false,
})
export class TableComponent extends Table implements OnDestroy {
    private _destroy: Subject<void> = new Subject<void>();
    private initialDisplayedColumns: TableColumnDef[];

    @Output() rowsWithSelectedData: EventEmitter<any[]> = new EventEmitter();
    @Input() formStatus: Signal<FormControlStatus> = signal('DISABLED');

    @Input() filter: boolean = false;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    @Input() tableName: string;
    textForm = new FormGroup({});
    checkBoxForm = new FormGroup({});
    formSubscription: Subscription;

    textFormFields: WritableSignal<FormlyFieldConfig[]> = signal(null);
    checkBoxFormFields: WritableSignal<FormlyFieldConfig[]> = signal(null);
    executeActionSignal: WritableSignal<any> = signal(null);
    executeActionSignalForOrder: WritableSignal<any> = signal(null);

    textModel: any = {
        filterValue: '',
    };
    checkboxModel: any = {
        columns: [],
    };

    showCheckboxes: boolean = false;

    _translocoContent: any;

    constructor(
        _liveAnnouncer: LiveAnnouncer,
        _injector: Injector,
        private dialog: DialogBuilderService,
        private filterService: TableFilterService,
        private tableStateService: TableStateService,
        private translate: TranslocoService
    ) {
        super(_liveAnnouncer, _injector);
        this._translocoContent = toSignal(translate.selectTranslation());

        this.translate.langChanges$.pipe(takeUntil(this._destroy)).subscribe(() => {
            this.setUpTextField();
        });

        this.subscribeToTextForm();
        this.subscribeToCheckboxForm();

        effect(() => {
            const data = this.dataSource();
            this.filterService.setModel(data.data);
            this.setupState();
            this.setUpCheckBoxFormFields(false);
            this.showCheckboxes = false;
        });

        effect(() => {
            this.executeActionSignal();
            this.applyFilter(this.filterService.model);
        });
    }

    toggleFilter(): void {
        this.showCheckboxes = !this.showCheckboxes;
        this.setUpCheckBoxFormFields(this.showCheckboxes);
    }

    getColumnfilterValues(): any {
        Object.keys(this.checkboxModel.columns);
    }

    ngOnDestroy(): void {
        this._destroy.next();
        this._destroy.complete();
    }

    getColumnNamesFromData(data: any[]): string[] {
        if (data.length === 0) {
            return [];
        }

        const extractKeys = (obj: any, prefix = ''): string[] => {
            return Object.keys(obj).flatMap(key => {
                const fullKey = prefix ? `${prefix}.${key}` : key;
                return typeof obj[key] === 'object' && obj[key] !== null
                    ? extractKeys(obj[key], fullKey) // Recursively extract nested keys
                    : fullKey;
            });
        };

        return extractKeys(data[0]); // Extract keys from the first item
    }

    private setUpTextField(): void {
        this.textFormFields.set([
            {
                key: 'filterValue',
                type: 'input',
                props: {
                    label: this._translocoContent()['grid.filterTitle'],
                    placeholder: 'Filter Value',
                },
            },
        ]);
    }

    private setUpCheckBoxFormFields(showCheckboxes: boolean): void {
        // const data = this.getColumnNamesFromData(this.dataSource().data);
        const data = this.initialDisplayedColumns.map(
            (column: TableColumnDef) => column.columnName
        );
        const fields: FormlyFieldConfig[] = [];

        if (showCheckboxes) {
            fields.push({
                key: 'columns',
                type: 'multicheckbox',
                templateOptions: {
                    options: data.map((columnName: string) => ({
                        label: columnName,
                        value: columnName,
                    })),
                },
                defaultValue: { type: true },
            });
        }

        this.checkBoxFormFields.set(fields);
    }

    private setupState(): void {
        const data = this.displayedColumns.map((column: TableColumnDef) => column.columnName);
        const state = this.tableStateService.getState(this.tableName);
        this.initialDisplayedColumns = data.map((colName: string) => {
            return {
                columnKey: colName,
                columnName: colName,
            };
        });

        if (state) {
            this.checkboxModel.columns = state.formState.columns;
            this.displayedColumns = state.displayedColumns;
        } else {
            this.checkboxModel.columns = data.reduce((acc, curr) => {
                acc[curr] = true;
                return acc;
            }, {});
        }
    }

    onDropdownSelection(col: TableColumnDef, row: any): void {
        if (row[col.selectionProperty]) {
            this.selection.select(row);
        } else {
            this.selection.deselect(row);
        }

        if (col.multiple && row[col.selectionProperty].includes(undefined)) {
            row[col.selectionProperty] = [undefined];
        }

        this.rowsWithSelectedData.emit(this.dataSource().data);
    }
    onRowDblClick(row): void {
        if (this.selection.isSelected(row)) {
            // Deselect the row if it's already selected
            this.selection.deselect(row);
        } else {
            // Select the row if it's not selected
            this.selection.select(row);
        }
        // Emit the updated data source with selected rows
        this.rowsWithSelectedData.emit(this.dataSource().data);
    }

    openFilterDialog(column, event): void {
        let targetAttr = event.target.getBoundingClientRect();
        let columnValues = [
            ...new Set(this.dataSource().data.map((item: any) => item[column.columnKey])),
        ];

        let dialogModel: TableFilterComponentModel = {
            key: column.columnKey,
            columnValues,
            selectedValues: [],
        };

        this.dialog.openDialog({
            config: {
                position: {
                    top: targetAttr.y + targetAttr.height + 10 + 'px',
                    left: targetAttr.x - targetAttr.width - 20 + 'px',
                },
            },
            descriptor: {
                dialogSize: 's',
                executeActionSignal: this.executeActionSignal,
                content: {
                    componentConfig: {
                        component: TableFilterComponentComponent,
                        componentData: {
                            model: dialogModel,
                        },
                    },
                },

                actions: {
                    dialogActions: [
                        {
                            code: 'cancel',
                            style: ButtonType.simple,
                            title: this._translocoContent()['grid.cancel'],
                            dismiss: true,
                        },
                        {
                            code: 'submit',
                            style: ButtonType.raised,
                            color: 'primary',
                            title: this._translocoContent()['grid.apply'],
                            dismiss: true,
                        },
                    ],
                },
                header: {
                    title: this._translocoContent()['grid.filterTitle'],
                },
            },
        });
    }

    openColumnOrderDialog(): void {
        const dialog = this.dialog.openDialog({
            descriptor: {
                header: {
                    title: this._translocoContent()['grid.tooltip'],
                    showCloseButton: true,
                },
                content: {
                    componentConfig: {
                        component: ColumnOrderComponent,
                        componentData: {
                            model: {
                                columns: structuredClone(this.displayedColumns),
                                tableId: this.tableName,
                            },
                        },
                    },
                },
                actions: {
                    dialogActions: [
                        {
                            code: 'cancel',
                            style: ButtonType.simple,
                            title: this._translocoContent()['grid.cancel'],
                            dismiss: true,
                        },
                        {
                            code: 'submit',
                            style: ButtonType.raised,
                            color: 'primary',
                            title: this._translocoContent()['grid.apply'],
                            manualClose: true,
                        },
                    ],
                },
                executeActionSignal: this.executeActionSignalForOrder,
                dialogSize: 'm',
            },
        });

        dialog
            .beforeClosed()
            .pipe(takeUntil(this._destroy))
            .subscribe(result => {
                if (result) {
                    this.displayedColumns = result.columns;
                    this.applyFilter(this.filterService.model);
                }
            });
    }

    applyFilter(model: TableAdvencedFilterModel): void {
        let filters = this.filterService.getSelectedFilterValuesAsArray();
        let filteredData = this.data.data.filter(item => {
            return Object.keys(filters).every(filterCategory => {
                const selectedValues = filters[filterCategory];

                // Check if any selected value in the filter category matches the item's value
                if (selectedValues.length === 0) return true; // If no values are selected, ignore the filter
                return selectedValues.includes(item[filterCategory]);
            });
        });

        this.dataSource().data = filteredData;
    }

    private subscribeToTextForm(): void {
        this.textForm.valueChanges.pipe(takeUntil(this._destroy)).subscribe(() => {
            const filterValue = this.textModel.filterValue
                ? this.textModel.filterValue.toLowerCase()
                : '';

            // Recursive function to flatten object values into an array of strings
            const extractValues = (obj: any): string[] => {
                let values: string[] = [];

                Object.keys(obj).forEach(key => {
                    if (typeof obj[key] === 'string') {
                        values.push(obj[key].toLowerCase());
                    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                        values = values.concat(extractValues(obj[key])); // Recursively extract values
                    }
                });

                return values;
            };

            // Filter the data based on filterValue
            const filteredData = this.data.data.filter(item => {
                const values = extractValues(item); // Get all values as an array of strings
                return values.some(value => value.includes(filterValue)); // Check if any value contains filter string
            });

            // Update the MatTableDataSource with the filtered data
            this.dataSource().data = filteredData;

            // Ensure paginator is in sync by calling firstPage() if needed
            if (this.paginator) {
                this.paginator.firstPage(); // Reset to the first page after applying filter
            }
        });
    }

    private subscribeToCheckboxForm(): void {
        this.checkBoxForm.valueChanges.pipe(takeUntil(this._destroy)).subscribe(() => {
            // Get selected columns (checkboxes) from the form model
            const selectedColumns = this.checkboxModel.columns; // { key: true/false }

            // Generate a list of column keys where the value is true (checkbox selected)
            const selectedColumnKeys = Object.keys(selectedColumns).filter(
                key => selectedColumns[key]
            );

            // Check if any checkbox is checked (if none are checked, apply filter to the whole data)
            const anyColumnSelected = selectedColumnKeys.length > 0;

            if (anyColumnSelected) {
                // Function to match selected column keys, supporting nested keys like "product.name"
                const getColumnDefinition = (key: string) => {
                    return this.initialDisplayedColumns.find(
                        (column: TableColumnDef) => column.columnKey === key
                    );
                };

                // Dynamically update the columns to be displayed
                this.displayedColumns = selectedColumnKeys
                    .map(getColumnDefinition)
                    .filter(column => column !== undefined); // Remove undefined entries

                // Save table state
                this.tableStateService.save({
                    name: this.tableName,
                    displayedColumns: this.displayedColumns,
                    formState: this.checkboxModel,
                });
            }
        });
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
    }
}
